import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import Toast from '../components/common/Toast';

// Create context
const NotificationContext = createContext();

// Notificação de preço do Bitcoin desativada temporariamente por solicitação do usuário.

/**
 * Hook to use the notification system
 * ✅ PHASE 1: Centralized notification system in English
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Notification system provider
 * ✅ PHASE 1: Optimized dual notification system
 * ✅ AUDIT 2: Implementada flexibilidade com clareza - cada função faz o que promete
 */
export const NotificationProvider = ({ children }) => {
  const [toastNotifications, setToastNotifications] = useState([]);
  const [cardNotifications, setCardNotifications] = useState([]);

  const generateUniqueId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // ✅ ESPECÍFICAS: Funções que fazem APENAS o que prometem
  const addToastNotification = useCallback((notification) => {
    const newNotification = {
      id: generateUniqueId(),
      ...notification,
      duration: 0,
      autoClose: false,
      timestamp: new Date().toISOString()
    };

    setToastNotifications(prev => [...prev, newNotification]);
    return newNotification.id;
  }, [generateUniqueId]);

  const addCardNotification = useCallback((notification) => {
    const newNotification = {
      id: generateUniqueId(),
      ...notification,
      duration: notification.duration !== undefined ? notification.duration : 5000,
      autoClose: notification.autoClose !== undefined ? notification.autoClose : true,
      timestamp: new Date().toISOString()
    };

    setCardNotifications(prev => [...prev, newNotification]);
    return newNotification.id;
  }, [generateUniqueId]);

  const addNotification = useCallback((notification) => {
    const displayType = notification.displayType || 'card';

    if (displayType === 'toast') {
      return addToastNotification(notification);
    } else {
      return addCardNotification(notification);
    }
  }, [addToastNotification, addCardNotification]);

  // Function to remove toast notification
  const removeToastNotification = useCallback((id) => {
    setToastNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Function to remove card notification
  const removeCardNotification = useCallback((id) => {
    setCardNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Function to remove notification (backward compatibility)
  const removeNotification = useCallback((id) => {
    removeToastNotification(id);
    removeCardNotification(id);
  }, [removeToastNotification, removeCardNotification]);

  // Function to clear all notifications
  const clearNotifications = useCallback(() => {
    setToastNotifications([]);
    setCardNotifications([]);
  }, []);

  const showSuccess = useCallback((title, message, options = {}) =>
    addCardNotification({ type: 'success', title, message, ...options }), [addCardNotification]);

  const showError = useCallback((title, message, options = {}) =>
    addCardNotification({ type: 'error', title, message, ...options }), [addCardNotification]);

  const showWarning = useCallback((title, message, options = {}) =>
    addCardNotification({ type: 'warning', title, message, ...options }), [addCardNotification]);

  const showInfo = useCallback((title, message, options = {}) =>
    addCardNotification({ type: 'info', title, message, ...options }), [addCardNotification]);

  const showLoading = useCallback((title, message, options = {}) =>
    addCardNotification({ type: 'loading', title, message, showProgress: false, ...options }), [addCardNotification]);

  // ✅ CORREÇÃO: Funções "Toast" fazem APENAS toast (como o nome promete)
  const showToastSuccess = useCallback((title, message, options = {}) => 
    addToastNotification({ type: 'success', title, message, ...options }), [addToastNotification]);

  const showToastError = useCallback((title, message, options = {}) => 
    addToastNotification({ type: 'error', title, message, ...options }), [addToastNotification]);

  const showToastWarning = useCallback((title, message, options = {}) => 
    addToastNotification({ type: 'warning', title, message, ...options }), [addToastNotification]);

  const showToastInfo = useCallback((title, message, options = {}) => 
    addToastNotification({ type: 'info', title, message, ...options }), [addToastNotification]);

  // ✅ CORREÇÃO: Funções "Card" fazem APENAS card (como o nome promete)
  const showCardSuccess = useCallback((title, message, options = {}) => 
    addCardNotification({ type: 'success', title, message, ...options }), [addCardNotification]);

  const showCardError = useCallback((title, message, options = {}) => 
    addCardNotification({ type: 'error', title, message, ...options }), [addCardNotification]);

  const showCardWarning = useCallback((title, message, options = {}) => 
    addCardNotification({ type: 'warning', title, message, ...options }), [addCardNotification]);

  const showCardInfo = useCallback((title, message, options = {}) => 
    addCardNotification({ type: 'info', title, message, ...options }), [addCardNotification]);

  const showTransactionStarted = useCallback((transactionType, amount, options = {}) => {
    return addCardNotification({
      type: 'loading',
      title: 'Processing Transaction',
      message: `Executing ${transactionType.toUpperCase()} of ${amount}...`,
      showProgress: false,
      details: { type: transactionType, amount, status: 'processing' },
      ...options
    });
  }, [addCardNotification]);

  const showTransactionCompleted = useCallback((transactionType, amount, balanceChange, options = {}) => {
    const details = { type: transactionType, amount, status: 'completed' };
    if (Math.abs(balanceChange) > 0.01) {
      details.balance_change = `${balanceChange > 0 ? '+' : ''}$${Math.abs(balanceChange).toFixed(2)}`;
    }
    return addCardNotification({
      type: 'transaction',
      title: 'Transaction Completed',
      message: `${transactionType.toUpperCase()} of ${amount} executed successfully!`,
      details,
      actions: [{ label: 'View Details', onClick: options.onViewDetails || (() => {}), primary: false }],
      ...options
    });
  }, [addCardNotification]);

  const showBalanceUpdate = useCallback((oldBalance, newBalance, options = {}) => {
    const change = newBalance - oldBalance;
    const changeText = change > 0 ? `+$${change.toFixed(2)}` : `-$${Math.abs(change).toFixed(2)}`;
    return addCardNotification({
      type: 'balance_update',
      title: 'Caixa Atualizado',
      message: `Saldo alterado em ${changeText}`,
      details: { previous: `$${oldBalance.toFixed(2)}`, current: `$${newBalance.toFixed(2)}`, change: changeText },
      ...options
    });
  }, [addCardNotification]);

  const showDataRefreshed = useCallback((duration, options = {}) => {
    return addCardNotification({
      type: 'info',
      title: 'Data Updated',
      message: `Information synchronized in ${duration}ms`,
      details: { sync_time: `${duration}ms`, timestamp: new Date().toLocaleTimeString() },
      ...options
    });
  }, [addCardNotification]);

  const value = useMemo(() => ({
    toastNotifications,
    cardNotifications,
    notifications: toastNotifications,

    addNotification,
    addToastNotification,
    addCardNotification,

    removeNotification,
    removeToastNotification,
    removeCardNotification,
    clearNotifications,

    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,

    showTransactionStarted,
    showTransactionCompleted,
    showBalanceUpdate,
    showDataRefreshed,

    showToastSuccess,
    showToastError,
    showToastWarning,
    showToastInfo,
    showCardSuccess,
    showCardError,
    showCardWarning,
    showCardInfo,
  }), [
    toastNotifications,
    cardNotifications,
    addNotification,
    addToastNotification,
    addCardNotification,
    removeNotification,
    removeToastNotification,
    removeCardNotification,
    clearNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    showTransactionStarted,
    showTransactionCompleted,
    showBalanceUpdate,
    showDataRefreshed,
    showToastSuccess,
    showToastError,
    showToastWarning,
    showToastInfo,
    showCardSuccess,
    showCardError,
    showCardWarning,
    showCardInfo,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      
      {/* Render Toast notifications (persistentes) */}
      <div className="fixed top-20 right-4 z-[60] flex flex-col gap-3 pointer-events-none max-w-sm w-full">
        {toastNotifications.map((notification) => (
          <Toast
            key={notification.id}
            {...notification}
            onClose={() => removeToastNotification(notification.id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export default NotificationContext;